/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from './AuthContext'
import { API_BASE } from '@/lib/api'

// Types
interface EntityPermissions {
    entityId: string
    entityName: string
    internalName?: string | null
    roleName: string
    permissions: string[]
}

interface EntityContextType {
    currentEntity: EntityPermissions | null
    entities: EntityPermissions[]
    setCurrentEntity: (entity: EntityPermissions) => void
    hasPermission: (permission: string) => boolean
    isLoading: boolean
}

const EntityContext = createContext<EntityContextType | undefined>(undefined)

interface EntityProviderProps {
    children: ReactNode
}

export function EntityProvider({ children }: EntityProviderProps) {
    const { entities, isAuthenticated, user } = useAuth()
    const queryClient = useQueryClient()
    const [currentEntity, setCurrentEntityState] = useState<EntityPermissions | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [initialized, setInitialized] = useState(false)

    // Read last_entity_id from AuthContext preferences (already fetched by /auth/me)
    useEffect(() => {
        if (!isAuthenticated || entities.length === 0) {
            setIsLoading(false)
            return
        }

        if (initialized) return

        // Override prioritaire : `?entity=<id>` dans l'URL. Pattern utilisé
        // par DirectoryPage (DIR-001) lors de l'ouverture d'une fiche
        // héritée dans un nouvel onglet — le nouvel onglet doit forcément
        // démarrer sur l'entité de l'onglet d'origine pour que la page
        // détail détecte correctement l'héritage et active le mode
        // lecture seule (`<InheritedBanner>`).
        const urlEntityId = new URLSearchParams(window.location.search).get('entity')
        const urlEntity = urlEntityId
            ? entities.find(e => e.entityId === urlEntityId)
            : null

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const savedId = (user?.preferences as any)?.global?.last_entity_id as string | undefined

        const savedEntity = savedId
            ? entities.find(e => e.entityId === savedId)
            : null

        const entityToUse = urlEntity || savedEntity || entities[0]
        setCurrentEntityState(entityToUse)

        // If no saved preference, save the first entity as default. Si
        // l'override URL est utilisé, on persiste aussi pour cohérence
        // intra-session (le user qui a ouvert la fiche héritée veut
        // continuer à voir cette entité dans cet onglet).
        if (entityToUse && (!savedId || urlEntity)) {
            saveEntityPreference(entityToUse.entityId)
        }

        setIsLoading(false)
        setInitialized(true)
    }, [isAuthenticated, entities, user, initialized])

    // Save entity preference to API
    const saveEntityPreference = async (entityId: string) => {
        try {
            await fetch(`${API_BASE}/auth/preferences`, {
                method: 'PATCH',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    global: {
                        last_entity_id: entityId,
                    },
                }),
            })
        } catch (error) {
            console.error('Failed to save entity preference:', error)
        }
    }

    const setCurrentEntity = useCallback((entity: EntityPermissions) => {
        setCurrentEntityState(entity)
        // Persist to API (fire and forget)
        saveEntityPreference(entity.entityId)
    }, [])

    // Invalidate all cached queries AFTER React has re-rendered with the new entityId.
    // This must be in a useEffect (not in setCurrentEntity) because components need
    // to re-render first so their queryFn closures capture the new entityId before
    // the refetch fires. Without this, queries refetch with the OLD entityId.
    const prevEntityIdRef = useRef<string | null>(null)
    useEffect(() => {
        const newId = currentEntity?.entityId ?? null
        if (prevEntityIdRef.current !== null && prevEntityIdRef.current !== newId) {
            // Entity changed (not initial load). DEUX gestes, et l'ordre compte.
            //
            // On JETTE d'abord ce que plus aucun composant ne regarde. `gcTime`
            // garde 5 minutes de requêtes démontées (main.tsx) : sans ce geste,
            // la relance ci-dessous les rejouait TOUTES — sur le module matériel,
            // une session ordinaire en empile une vingtaine (listing, facettes,
            // agrégats, groupes, référentiel, fiche, contrôles, exemplaires,
            // calendrier…). Cette rafale ne rencontre PAS « un pool de 36 » :
            // 36 est le budget de FLOTTE (4 instances × `POOL_MAX_SIZE = 8`,
            // plus le scheduler — `backend/src/core/database.py`), et une
            // instance n'en ouvre que HUIT. Surtout, la base n'a qu'UN vCPU :
            // au-delà d'une dizaine de requêtes réellement simultanées, elles ne
            // s'exécutent plus en parallèle, elles font la queue DANS
            // PostgreSQL, où l'attente dégrade tous les tenants. Les jeter ne
            // déclenche aucun appel, et rien de l'entité quittée ne pourra être
            // resservi tel quel au prochain montage.
            queryClient.removeQueries({ predicate: (query) => query.getObserversCount() === 0 })
            // Puis on rejoue ce qui reste, c'est-à-dire ce qui est à l'écran.
            //
            // ⚠️ Ne cherchez PAS la protection dans `refetchType` : elle n'y est
            // pas. `refetchQueries` écarte `query.isDisabled()`, qui vaut
            // `!isActive()` dès qu'il y a un observateur — TanStack REFUSE de
            // rejouer une requête MONTÉE mais désactivée (`enabled: false`),
            // `'all'` exactement comme `'active'` (mesuré sur query-core
            // 5.90.20). Après le geste ci-dessus, tout ce qui reste a un
            // observateur, donc les deux valeurs sélectionnent le même ensemble :
            // `'all'` dit seulement l'intention, « rejouer tout ce qui reste ».
            //
            // Ce qui rattrape la requête désactivée est la marque `invalidate()`,
            // que `invalidateQueries` pose sur TOUTES les requêtes et que
            // `refetchType` ne filtre pas : elle affiche encore l'entité quittée
            // tant qu'elle est désactivée, mais elle est périmée, donc elle se
            // rejoue à la réactivation au lieu de resservir.
            //
            // Gardien des trois faits :
            // `contexts/__tests__/uneBasculeDentiteNeRejoueQueCeQuiEstAffiche.test.tsx`.
            queryClient.invalidateQueries({ refetchType: 'all' })
        }
        prevEntityIdRef.current = newId
    }, [currentEntity?.entityId, queryClient])

    const hasPermission = useCallback((permission: string): boolean => {
        if (!currentEntity) return false
        return currentEntity.permissions.includes(permission)
    }, [currentEntity])

    const value = useMemo<EntityContextType>(() => ({
        currentEntity,
        entities,
        setCurrentEntity,
        hasPermission,
        isLoading,
    }), [currentEntity, entities, setCurrentEntity, hasPermission, isLoading])

    return (
        <EntityContext.Provider value={value}>
            {children}
        </EntityContext.Provider>
    )
}

export function useEntity(): EntityContextType {
    const context = useContext(EntityContext)
    if (context === undefined) {
        throw new Error('useEntity must be used within an EntityProvider')
    }
    return context
}

/**
 * L'identifiant de l'entité COURANTE, ou `null` hors d'un `EntityProvider` ou avant son choix.
 * Pour un composant partagé qui la COMPARE sans en dépendre — la fenêtre d'un champ
 * personnalisé nomme l'entité de la fiche quand ce n'est pas celle-ci (CUSTOM-013).
 * ⛔ Jamais pour écrire : une écriture lit `useEntity`, qui refuse de deviner.
 */
export function useEntiteCouranteSiConnue(): string | null {
    return useContext(EntityContext)?.currentEntity?.entityId ?? null
}
